import { appAxios } from '@/network/base/appAxios';
import { ArrowLeft, X, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { uploadToS3 } from '@/utils/s3presigned';
import { toast } from 'sonner';

const AuthorizationModal = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [authData, setAuthData] = useState({
    role: '',
    licenseHolderName: '',
    contractorLicense: '',
    fullName: '',
    propertyAddress: '',
    governmentId: null,
    governmentIdKey: null,
    certifications: {
      legal: false,
      understanding: false
    }
  });
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const handleContinueStep1 = () => setStep(2);

  const handleSelectRole = (role) => {
    setAuthData({ ...authData, role });
  };

  const handleContinueStep2 = () => {
    if (authData.role) setStep(3);
  };

  const handleInputChange = (field, value) => {
    setAuthData({ ...authData, [field]: value });
  };

  const handleCertificationChange = (cert) => {
    setAuthData({
      ...authData,
      certifications: {
        ...authData.certifications,
        [cert]: !authData.certifications[cert]
      }
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const { data, error } = await uploadToS3(file, 'authorization-documents');

      if (error) {
        console.error('Failed to upload file:', error);
        toast.error('Failed to upload file. Please try again.');
        setIsUploading(false);
        return;
      }

      setAuthData({
        ...authData,
        governmentId: file,
        governmentIdKey: data.key
      });
    } catch (error) {
      setIsUploading(false);
      console.error('Upload error:', error);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setAuthData({
      ...authData,
      governmentId: null,
      governmentIdKey: null
    });
  };

  const handleFinalSubmit = async () => {
    let data = {};
    if (authData.role === 'contractor') {
      data = {
        user_type: authData.role,
        license_number: authData.contractorLicense,
        license_holder_name: authData.licenseHolderName,
        owner_name: "N/A", propery_address: "N/A", government_id: "N/A"
      }
    }
    else if (authData.role === 'home_owner') {
      data = {
        user_type: authData.role,
        owner_name: authData.fullName,
        property_address: authData.propertyAddress,
        government_id: authData.governmentIdKey,
        license_number: "N/A", license_holder_name: "N/A"
      }
    }

    try {
      const response = await appAxios.post('/Authorization/v1.0/add-authorization', data);
      if (response.status === 200) {
        toast.success('Authorization submitted successfully');
        onSubmit();
      }
    } catch (error) {
      console.error('Failed to submit authorization:', error);
      toast.error('Failed to submit authorization. Please try again.');
    }
  };

  const canSubmitStep3 = authData.role === 'contractor'
    ? authData.licenseHolderName && authData.contractorLicense && authData.certifications.legal && authData.certifications.understanding
    : authData.fullName && authData.propertyAddress && authData.governmentIdKey && authData.certifications.legal && authData.certifications.understanding;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
      <div className="bg-[#1a1a1a] rounded-lg p-8 max-w-md w-full mx-4 relative flex flex-col justify-center items-center">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="absolute top-4 left-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}

        <h2 className="text-2xl font-semibold text-white text-center my-5">
          Authorization Required
        </h2>

        {step === 1 && (
          <>
            <p className="text-gray-300 text-center mb-6 max-w-xs mx-auto">
              For security purposes, please verify your identity to continue.
            </p>
            <button
              onClick={handleContinueStep1}
              className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg mb-3"
            >
              Continue
            </button>
            <button
              onClick={onClose}
              className="w-full bg-transparent border border-gray-600 hover:border-gray-500 text-white py-3 rounded-lg"
            >
              Cancel
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-gray-300 text-center mb-6">
              Please select who is completing the permit submission?
            </p>
            <button
              onClick={() => handleSelectRole('home_owner')}
              className={`w-full py-3 rounded-lg mb-3 ${authData.role === 'home_owner'
                ? 'bg-white text-black'
                : 'bg-black border border-gray-600 text-white hover:border-gray-500'
                }`}
            >
              I am a Homeowner
            </button>
            <button
              onClick={() => handleSelectRole('contractor')}
              className={`w-full py-3 rounded-lg mb-6 ${authData.role === 'contractor'
                ? 'bg-white text-black'
                : 'bg-black border border-gray-600 text-white hover:border-gray-500'
                }`}
            >
              I am a Contractor
            </button>
            <button
              onClick={handleContinueStep2}
              disabled={!authData.role}
              className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </>
        )}

        {step === 3 && authData.role === 'contractor' && (
          <>
            <input
              type="text"
              placeholder="License Holder Name"
              value={authData.licenseHolderName}
              onChange={(e) => handleInputChange('licenseHolderName', e.target.value)}
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg mb-3 placeholder-gray-500"
            />
            <input
              type="text"
              placeholder="Contractor's License #"
              value={authData.contractorLicense}
              onChange={(e) => handleInputChange('contractorLicense', e.target.value)}
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg mb-4 placeholder-gray-500"
            />
            <div className='my-2'>
              <label className="flex items-start mb-3 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={authData.certifications.legal}
                  onChange={() => handleCertificationChange('legal')}
                  className="mt-1 mr-3"
                />
                <span>
                  I certify that I am a licensed contractor and have been authorized by the property owner to submit permit applications, updates, or revisions through CityPermit.ai for the project listed.
                </span>
              </label>
              <label className="flex items-start mb-6 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={authData.certifications.understanding}
                  onChange={() => handleCertificationChange('understanding')}
                  className="mt-1 mr-3"
                />
                <span>
                  I understand that the City will issue all permits directly, and CityPermit.ai is acting solely as a submission facilitator.
                </span>
              </label>
            </div>
            <button
              onClick={handleFinalSubmit}
              disabled={!canSubmitStep3}
              className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </>
        )}

        {step === 3 && authData.role === 'home_owner' && (
          <>
            <input
              type="text"
              placeholder="Full Name"
              value={authData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg mb-3 placeholder-gray-500"
            />
            <input
              type="text"
              placeholder="Property Address"
              value={authData.propertyAddress}
              onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg mb-3 placeholder-gray-500"
            />
            {!authData.governmentId ? (
              <>
                <div className='w-full flex items-center gap-3 mb-4'>
                  <label
                    htmlFor="file-upload"
                    className={`w-12 h-12 flex-shrink-0 bg-black border border-gray-700 rounded-lg flex items-center justify-center text-white text-2xl ${isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-600'
                      }`}
                  >
                    {isUploading ? '...' : '+'}
                  </label>

                  <div className="flex flex-col justify-center text-left">
                    <span className="text-white">
                      Upload a valid Government-Issued ID
                    </span>
                    <span className="text-xs text-gray-500">
                      (Driver's License, State ID, Passport, or any official photo ID)
                    </span>
                  </div>
                </div>

                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf"
                  disabled={isUploading}
                />

                {isUploading && (
                  <p className="text-blue-400 text-sm mb-3">Uploading...</p>
                )}
              </>
            ) : (
              <div className="w-full bg-black border border-gray-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-white text-sm">{authData.governmentId.name}</span>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-400"
                  title="Remove file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className='my-2'>
              <label className="flex items-start mb-3 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={authData.certifications.legal}
                  onChange={() => handleCertificationChange('legal')}
                  className="mt-1 mr-3"
                />
                <span>
                  I certify that I am the legal property owner of this address and authorize CityPermit.ai to prepare, process, and submit this permit application or resubmission on my behalf.
                </span>
              </label>
              <label className="flex items-start mb-6 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={authData.certifications.understanding}
                  onChange={() => handleCertificationChange('understanding')}
                  className="mt-1 mr-3"
                />
                <span>
                  I acknowledge that all permits are issued exclusively by the City, and CityPermit.ai’s role is to assist with preparation and submission.
                </span>
              </label>
            </div>
            <button
              onClick={handleFinalSubmit}
              disabled={!canSubmitStep3}
              className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthorizationModal;

